/*
 * ProtectedRoute Component - Enhanced with profile completion check
 * 
 * Changes made:
 * - Added profile completion check after authentication
 * - Shows ProfileCompletionForm when profile is incomplete
 * - Refreshes profile context after completion
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import type { UserRole } from '../../lib/supabase';
import { getCurrentProfile, isProfileComplete, supabase } from '../../lib/supabase';
import { ProfileCompletionForm } from './ProfileCompletionForm';

interface ProtectedRouteProps {
  children: React.ReactNode;
  role: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, role }) => {
  const { profile, refreshProfile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<{ id: string; role?: UserRole } | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      
      try {
        // Get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Get the user's profile from the database to check their role
        const profileData = await getCurrentProfile();
        
        if (profileData) {
          setUser({ 
            id: session.user.id, 
            role: profileData.role 
          });
          
          // Check if the user's role matches the required role for this route
          const roleMatches = profileData.role === role;
          setAuthorized(roleMatches);
          
          // If authorized, check profile completion
          if (roleMatches) {
            setCheckingCompletion(true);
            try {
              const complete = await isProfileComplete();
              setProfileComplete(complete);
            } catch (error) {
              console.error('Error checking profile completion:', error);
              // If we can't check, assume incomplete to be safe
              setProfileComplete(false);
            } finally {
              setCheckingCompletion(false);
            }
          }
        } else {
          setUser({ id: session.user.id });
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        checkAuth();
      } else {
        setUser(null);
        setAuthorized(false);
        setProfileComplete(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [role]);

  const handleProfileCompletion = async () => {
    try {
      // Refresh the profile context to get updated data
      await refreshProfile();
      
      // Re-check completion status
      const complete = await isProfileComplete();
      setProfileComplete(complete);
      
      console.log('Profile completion status after update:', complete);
    } catch (error) {
      console.error('Error after profile completion:', error);
      // Fallback: assume complete if we can't check
      setProfileComplete(true);
    }
  };

  // Show loading spinner while checking auth and profile completion
  if (loading || checkingCompletion) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">
            {loading ? 'Authenticating...' : 'Checking profile...'}
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to appropriate role page if wrong role
  if (!authorized) {
    const redirectPath = user.role === 'coach' ? '/coach' : '/athlete';
    return <Navigate to={redirectPath} replace />;
  }

  // Show profile completion form if profile is incomplete
  if (!profileComplete) {
    return <ProfileCompletionForm onComplete={handleProfileCompletion} />;
  }

  // All checks passed - render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;